import React, {useContext, useEffect, useRef, useState} from 'react';
import ReactDOMServer from 'react-dom/server';
import mapboxgl from 'mapbox-gl';
import {MapContext} from "../context/MapContext";
import {loadFeaturesByAreaTypes} from "../services/map/mapFeatures";
import HeaderNav from "./_common/HeaderNav";
import LoaderSpinner from "./_common/Loader";
import {useHistory} from "react-router-dom";
import {fitToBoundsDockyard} from "../services/map/MapBoxUtils";
import {LayerSwitcherControl} from "../services/map/MapBoxControls";
import LayerSwitcherPanel from "./map/controls/LayerSwitcherPanel";
import {checkPositionWithinPolygon} from "../services/map/maputils";
import Notification from "./_common/Notification";
import errorIcon from "../assets/images/error-map-display-128px.png";
import PopUpFeatureInfo from "./_common/map/PopUpFeatureInfo";
import {usePosition} from 'use-position';
import mapLocale from "../ui/locale";
import {MAPBOX_STYLE} from "../services/map/mapboxParameters";


mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_API_KEY;
const delayNotificationOpened = process.env.REACT_APP_NOTIFICATION_DELAY_DEFAULT || 2000;

const Webmap = () => {
        const {mapContext, dispatch} = useContext(MapContext);
        const [map, setMap] = useState(mapContext.map)
        const [browserSupported, setBrowserSupported] = useState(true)
        const [progress, setProgress] = useState(false)
        const [dockyardAreas, setDockyardAreas] = useState(null)
        const [notificationDisplay, setNotificationDisplay] = useState(false)
        const [intersectedPolygons, setIntersectedPolygons] = useState(null)
        const [geolocate, setGeolocate] = useState(null)
        const [indicatorColor, setIndicatorColor] = useState(null)
        const timeout = useRef(null)
        const watch = true;
        const {
            latitude,
            longitude,
            error,
        } = usePosition(watch, {enableHighAccuracy: true});

        const [notificationConfig, setNotificationConfig] = useState({
            name: 'Test de titre',
            message: 'Test de message',
            color: '#afafaf'
        })
        const [layerSwitcherPanel, setLayerSwitcherPanel] = useState(
            (mapContext && mapContext.config.layerSwitcherPanel.open)
            || false
        )
        const [dockyardActive] = useState(mapContext.dockyardData || null)
        const history = useHistory();

        useEffect(() => {
            try {
                setProgress(true);
                let data;
                const timeout = setTimeout(() => {
                    (async () => {
                        setProgress(true);
                        /* Check if browser is supported */
                        if (!mapboxgl.supported()) {
                            setBrowserSupported(false)
                            setProgress(false);
                        } else {
                            mapContext.dockyardData &&
                            'dockyard_areas' in mapContext.dockyardData &&
                            setDockyardAreas(mapContext.dockyardData.dockyard_areas);
                            setProgress(false);
                            mapLoader()
                        }
                    })();
                }, 500);
                return () => {
                    clearTimeout(timeout);
                    if (data) {
                        data();
                    }
                };
            } catch (e) {
                console.log(e)
                setProgress(false);
            }
        }, []);

        useEffect(() => {
            let positionLngLat = [longitude, latitude]
            geofenceArea(positionLngLat)
        }, [latitude, longitude])

        useEffect(() => {
            if (error) {
                let message = '';
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        message = 'Impossible d\'accéder à votre position. Vérifiez que la fonctionnalité de "Position" est bien activée sur votre téléphone, ou que votre navigateur autorise l\'accès à la localisation'
                        break;
                    case error.POSITION_UNAVAILABLE:
                        message = 'Votre position est actuellement indisponible.'
                        break;
                    case error.TIMEOUT:
                        message = 'Votre position ne peut pas être actuellement récupéré.'
                        break;
                    case error.UNKNOWN_ERROR:
                        message = 'Une erreur inconnue ne permet pas d\'accéder à votre position.'
                        break;
                    default :
                        message = 'Impossible d\'accéder à votre position. Vérifiez que la fonctionnalité de "Position" est bien activée sur votre téléphone, ou que votre navigateur autorise l\'accès à la localisation'
                        break;
                }

                setNotificationConfig({
                        name: 'Geolocalisation impossible',
                        message: message,
                        color: "#000000"
                    }
                )
                setNotificationDisplay(true)
            }
        }, [error])

        useEffect(() => {
                try {
                    setLayerSwitcherPanel(mapContext.config.layerSwitcherPanel.open)
                } catch (e) {
                    console.log(e)
                }
            }
            , [mapContext.config.layerSwitcherPanel.open])

        useEffect(() => {
            if (intersectedPolygons && intersectedPolygons.length) {
                setNotificationDisplay(false)
                let polyIntersected = intersectedPolygons[0] || null
                polyIntersected && openConfigNotification(polyIntersected)
            } else {
                //close notification
                notificationDisplay && setNotificationDisplay(false)
                setIndicatorColor(null)
            }
        }, [intersectedPolygons])

        useEffect(() => {
            return () => {
                clearTimeout(timeout.current);
            }
        }, [notificationDisplay])

        const openConfigNotification = polygonNotificate => {
            setNotificationConfig({
                    name: polygonNotificate.area_type.name,
                    message: polygonNotificate.area_type.message,
                    color: polygonNotificate.area_type.color
                }
            )
            setIndicatorColor(polygonNotificate.area_type.color)
            setNotificationDisplay(true)
            timeout.current = setTimeout(
                () => {
                    setNotificationDisplay(false)
                },
                delayNotificationOpened);
        }

        //Load Map
        const mapLoader = () => {
            let map = new mapboxgl.Map({
                container: 'map',
                style: MAPBOX_STYLE,
                pitchWithRotate: false,
                animate: false,
                duration: 0,
                locale: mapLocale,
            });

            //Load context values if necessary
            mapContext.config.zoom.current && map.setZoom(mapContext.config.zoom.current);
            mapContext.config.zoom.maxZoom && map.setMaxZoom(mapContext.config.zoom.maxZoom);
            mapContext.config.zoom.minZoom && map.setMinZoom(mapContext.config.zoom.minZoom);

            map.setPitch(false)

            // Add geolocation control
            let geolocateCtrl = new mapboxgl.GeolocateControl({
                positionOptions: {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 10000
                },
                trackUserLocation: true,
                fitBoundsOptions: {
                    animate: false
                }
            })
            map.addControl(geolocateCtrl);
            setGeolocate(geolocateCtrl)

            //Fit Bounds on selected Dockyard
            dockyardActive && fitBoundsByDockyard(map, dockyardActive)

            // Add zoom and rotation controls to the map.
            map.addControl(new mapboxgl.NavigationControl());
            const layerSwitcherControl = new LayerSwitcherControl()
            map.addControl(layerSwitcherControl, 'top-right')
            dispatch({type: 'MAP_LOADED', map})
            setMap(map)
            // Clean up on unmount
            return () => map.remove();
        }
        /**
         * Set map View to dockyard from data
         * @param mapElement
         * @param dockyardData
         */
        const fitBoundsByDockyard = (mapElement, dockyardData) => {
            let fitBounds = (dockyardData && fitToBoundsDockyard(dockyardData)) || null
            fitBounds &&
            mapElement.fitBounds(fitBounds, {
                padding: 20,
                duration: 0 // Stop animation fly to
            });
        }

        /**
         * Add Elements following loading Map
         */
        map && map.on('load', async () => {
            //Add Dockyard Areas by layer
            dockyardAreas && await addDockyardLayers();
            // //Fit Bounds on selected Dockyard
            dockyardActive && fitBoundsByDockyard(map, dockyardActive)
            //Add a click event to toggle display panet layer switcher
            let mapBoxCtrlLayerSwitcher = document && document.querySelector('.mapboxgl-ctrl-custom-layer-switcher')
            mapBoxCtrlLayerSwitcher && mapBoxCtrlLayerSwitcher
                .addEventListener('click', () => {
                    dispatch({type: "TOGGLE_LAYER_SWITCHER_DISPLAY"})
                })
            //Active by default load map Geolocation
            mapContext && mapContext.config.geolocate && mapContext &&
            mapContext.config.geolocate.active &&
            geolocate.trigger();
        });


        /**
         * Set Data Layers from dockyards Area Data
         * @returns {Promise<void>}
         */
        const addDockyardLayers = async () => {
            let mapLayers = [];
            mapLayers = await loadFeaturesByAreaTypes(dockyardAreas)
            if (mapLayers) {
                for (let mapLayer of mapLayers) {
                    if (!map.getLayer(mapLayer.id)) {
                        map.addSource(
                            mapLayer.id,
                            mapLayer.source
                        );
                        map.addLayer(
                            mapLayer.layer
                        );
                        //Initialize visibility layer if not define
                        if (mapLayer.id && !mapContext.config.layerSwitcherPanel.layers[mapLayer.id]) {
                            dispatch({
                                type: 'TOGGLE_VISIBILITY_LAYER', layerVisivility: {
                                    [mapLayer.id]: 'visible',
                                }
                            })
                        }
                        //Apply visibility from context
                        map.setLayoutProperty(mapLayer.id, 'visibility',
                            mapContext.config.layerSwitcherPanel.layers[mapLayer.id] || 'visible'
                        );
                        await addPopUpInteractions(mapLayer.id)
                    }
                }
            }
        }

        /**
         * Add Pop information on click feature by layer
         * @param layerId
         */
        const addPopUpInteractions = async (layerId) => {
            map && map.on('click', layerId, (e) => {
                //Check if Popup is already opened, no display another if true
                if (checkPopUpOpened()) {
                    /** Convert JSX PopUp TO HTML string*/
                    let popupHTML = ReactDOMServer.renderToStaticMarkup(
                        <PopUpFeatureInfo featureData={e.features[0]}/>
                    )
                    e.features[0].properties.descr &&
                    new mapboxgl.Popup()
                        .setLngLat(e.lngLat)
                        .setHTML(
                            popupHTML || <p>Aucune information</p>
                        )
                        .addTo(map);
                }
            });
        }

        /**
         * Check if One popup is already Opened
         * @returns {boolean}
         */
        const checkPopUpOpened = () => {
            return document.querySelector(".mapboxgl-popup") === null || false
        }

        /**
         * Action to list dockyards selection
         */
        const backToDoSwitcher = () => {
            if (dockyardActive && dockyardActive.division_id) {
                history.push(`/chantiers/${dockyardActive.division_id}`)
            } else {
                history.push('/')
            }
        }

        /**
         * Check Geofence Area
         * @param e
         */
        const geofenceArea = (e) => {
            if (dockyardAreas) {
                let polyIntersects = checkPositionWithinPolygon(dockyardAreas, e) || [];
                let polyNotifications = (polyIntersects &&
                    polyIntersects.filter(polyIntersected => polyIntersected.area_type.level <= 0))
                if (intersectedPolygons && polyNotifications && intersectedPolygons[0] && polyNotifications[0]) {
                    polyNotifications[0] !== intersectedPolygons[0] &&
                    setIntersectedPolygons(polyNotifications)
                } else {
                    setIntersectedPolygons(polyNotifications)
                }
            } else {
                setIntersectedPolygons(null)
            }
        }

        return (
            <div className="container-fluid" id={'container-heading'}>
                <HeaderNav
                    indicator={true}
                    indicatorColor={indicatorColor}
                    searchAction={false}
                    title={(dockyardActive && dockyardActive.name) || 'Aucun chantier'}
                    backToDo={() => backToDoSwitcher()}
                />
                {
                    progress ?
                        <div className={`container-data ${progress && 'loader'}`}>
                            <LoaderSpinner/>
                        </div>
                        :
                        browserSupported ?
                            <>
                                <div id="map" className="map">
                                    <LayerSwitcherPanel
                                        openPanel={{show: layerSwitcherPanel, setShow: setLayerSwitcherPanel}}
                                    />
                                </div>
                                <Notification
                                    openNotif={{show: notificationDisplay, setShow: setNotificationDisplay}}
                                    config={notificationConfig}
                                />
                            </> :
                            <>
                                <div id="map" className={"error-container"}>
                                    <div>
                                        <img src={errorIcon} alt={"Erreur d'affichage de carte"}/>
                                        <div className='title-text-center'>Oops, votre navigateur ne supporte pas la
                                            cartographie.
                                        </div>
                                        <div className='global-text-center my-2'>Veuillez mettre à jour votre
                                            navigateur, ou
                                            en
                                            utiliser
                                            un autre comme Chrome ou Mozilla Firefox.
                                        </div>
                                        <button className={"btn btn-secondary btn-back"}
                                                onClick={() => history.push('/')}>
                                            Retour à l'accueil
                                        </button>
                                    </div>
                                </div>
                            </>
                }
            </div>
        );
    }
;

export default Webmap;